// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, TextField, Button, Box, Typography } from '@mui/material';
import api from '../utils/api';
import { useAuthStore } from '../store';
import { messageDeConnexion } from '../utils/connexion';
import { loginWithProof, getProof, PROOF_HEADER } from '../utils/preuveTravail';

// Adresses refusées depuis l'ouverture de la page : leurs essais suivants
// portent une preuve de travail (voir utils/preuveTravail.js).
const failures = new Set();
const readChallenge = (scope) => api.get('/public/defi', { params: { portee: scope } }).then((r) => r.data);

export default function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Au-delà du premier échec, la tentative porte une preuve de travail :
      // elle compte alors sur un budget que les échecs d'un tiers sans preuve
      // n'entament pas, et le back-office ne se ferme pas à son titulaire.
      const response = await loginWithProof({
        cle: formData.email,
        echecs: failures,
        envoyer: (proof) => api.post('/auth/login', formData,
          proof ? { headers: { [PROOF_HEADER]: proof } } : undefined),
        preuve: () => getProof(readChallenge, 'connexion'),
      });
      // Le jeton est stocké avant toute autre requête : l'intercepteur d'api.js
      // le lit dans localStorage.
      login(response.data.access_token);
      navigate('/');
    } catch (err) {
      // Un verrou (429) ou une saturation (503) n'est pas un mot de passe faux :
      // le motif du serveur est dit tel quel (utils/connexion.js).
      setError(messageDeConnexion(err));
    }
  };

  // Écran affiché sans la barre ni le conteneur de App.jsx : il porte lui-même
  // son centrage. La carte reprend celle de la connexion à la console de
  // gestion, du verre au milieu d'un fond vide.
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Paper sx={{ width: '100%', maxWidth: 400, p: 3.5 }}>
        <Typography
          variant="overline"
          component="div"
          align="center"
          sx={{ fontSize: 11, letterSpacing: '1px', color: 'secondary.main', lineHeight: 1.6 }}
        >
          Vigeos
        </Typography>
        <Typography
          variant="h1"
          align="center"
          sx={{ fontSize: 26, fontWeight: 700, mt: 0.5 }}
        >
          Espace équipe
        </Typography>
        <Typography variant="body2" align="center" sx={{ mt: 0.75, mb: 2.5, color: 'text.secondary' }}>
          Réservé aux comptes de l'équipe Vigeos
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Adresse électronique"
            type="email"
            margin="normal"
            autoComplete="username"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <TextField
            fullWidth
            label="Mot de passe"
            type="password"
            margin="normal"
            autoComplete="current-password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          />
          {error && (
            <Typography sx={{ mt: 1.5, fontSize: 13, color: 'error.main' }}>{error}</Typography>
          )}
          <Button fullWidth variant="contained" sx={{ mt: 2.5, py: 1.25 }} type="submit">
            Se connecter
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
